/**
 * Executive View wire types.
 *
 * Hand-maintained against the backend schemas:
 *  - Main API:    src/controllers/v2/executive.py  (options + config)
 *  - Agentic-API: app/controllers/v2/executive.py  (prompts + query)
 *
 * Design: executive-view-design/EXEC_VIEW_v1_design.md
 */
import { CalcProgressRow } from '../AgenticV2/types';

// All AI-enabled (tagged) scenarios of the brand's instance — there is
// no per-scenario executive whitelist or default flag.
export interface ExecutiveScenarioOption {
  scenario_id: number;
  name: string;
  tag: string | null;
}

export interface ExecutiveBrand {
  model_instance_id: number;
  model_name: string;
  model_instance_name: string;
  // Free-text decorator under the brand name (e.g. the indication).
  subheader: string | null;
  // Exec AI year window (C1): years before / after the current calendar year
  // the time-series charts + Excel exports are clipped to. null = show all
  // available years (the global default). Set in the Executive View Management
  // modal's config tab.
  exec_ai_years_before?: number | null;
  exec_ai_years_after?: number | null;
  // Present only when the backend has an obvious instance timestamp.
  updated_at?: string | null;
  scenarios: ExecutiveScenarioOption[];
}

export interface ExecutiveOptionsResponse {
  brands: ExecutiveBrand[];
}

// Nav destination within a brand workspace (design doc EXEC_WHATIF_v1_
// design.md §3 + the tornado destination). Owned here so index.tsx and
// Sidebar.tsx share one declaration.
export type ExecutiveDestination = 'query' | 'whatif' | 'tornado';

export interface ExecutivePromptItem {
  id: number;
  title: string;
  subtitle: string | null;
  category: string;
  sort_order: number;
}

/**
 * Tornado destination wire types.
 *
 * Backend: Agentic-API app/controllers/v2/executive.py (prompt listing
 * with tool=executive_tornado) and app/controllers/v2/executive_tornado.py
 * (run / poll / recent). A tornado prompt freezes the report definition;
 * the executive late-binds an ordered scenario pair (Baseline /
 * Compare-to) whose defaults are authored at publish time.
 */

export interface TornadoPromptDefaults {
  scenario_a_id: number;
  scenario_b_id: number;
  scenario_a_name: string | null;
  scenario_b_name: string | null;
}

export interface ExecutiveTornadoPromptItem extends ExecutivePromptItem {
  // null when the stored defaults could not be projected server-side —
  // the FE falls back to the brand's first two tagged scenarios.
  defaults: TornadoPromptDefaults | null;
}

/** The executive's ordered scenario pair. A = baseline every bar's effect
 * is computed against; B = comparison source. */
export interface TornadoPair {
  aId: number;
  bId: number;
}

export interface TornadoChartRow {
  ColumnName: string;
  ColumnValue: number;
  Groups?: Array<{ GroupName?: string; Value: number }>;
}

export interface TornadoRunResult {
  // [Scenario A summary, ...effects, Scenario B summary] — the same
  // ordering contract TornadoChartCore consumes (after ColumnValue→Value).
  chart_rows: TornadoChartRow[];
  output_name: string | null;
  columns: Array<{ name: string; inputs: Array<{ Name: string }> }> | null;
  scenario_a_name: string | null;
  scenario_b_name: string | null;
  // The frozen report's date range as labels ("2028", or "2026–2029" for a range), resolved server-side from
  // XREF_Date. Absent/null on runs completed before 2026-09-08.
  analysis_period?: string | null;
  calculated_at: string | null;
}

export interface TornadoRunProgress {
  phase?: 'calculating' | 'charting' | string;
  error?: string;
  // Per-group CalculationInvocation snapshot written by the executor on each poll tick while the worker runs
  // (same wire shape as WhatIfProgress.calc).
  calc?: CalcProgressRow[] | null;
}

export interface TornadoRunStatus {
  plan_id: string;
  status: 'executing' | 'completed' | 'failed' | string;
  prompt_id: number | null;
  model_instance_id: number | null;
  scenario_ids: number[] | null;
  progress: TornadoRunProgress | null;
  result?: TornadoRunResult;
}

/**
 * Response of POST /v2/executive/query. `chart_payload` is the same
 * enriched shape the analyst ResultCard carries ({data, request_context}),
 * so it feeds ChartVizResult unchanged.
 */
export interface ExecutiveQueryResponse {
  tool: 'chart_visualization';
  chart_payload: Record<string, unknown>;
  prompt_id: number;
  scenario_ids: number[];
}

/** Executive scenario selection bounds (mirrors the forecast API's cap). */
export const MIN_SCENARIOS = 1;
export const MAX_SCENARIOS = 3;

/**
 * QF RECENT tab row (design doc executive-view-design/EXEC_WHATIF_v1_
 * design.md §5, §8: GET /v2/executive/query/recent, deduped by prompt
 * server-side, newest first, cap 10). Carries prompt_id + title only —
 * the full ExecutivePromptItem is resolved from the already-loaded
 * prompts list by id at click time (QuestionLibrary.tsx); scenario_names
 * is display metadata only, never fed back into a query (late-binding
 * rule, §5).
 */
export interface ExecutiveQueryRecentRun {
  prompt_id: number;
  title: string;
  scenario_names: string[];
  inserted_at: string;
}

export interface ExecutiveQueryRecentResponse {
  runs: ExecutiveQueryRecentRun[];
}

/**
 * What-If Analysis wire types.
 *
 * Backend: Agentic-API `/v2/executive/whatif/*`
 * (design doc executive-view-design/EXEC_WHATIF_v1_design.md §7). The
 * backend is not landed yet — these shapes are implemented to the design
 * doc's contract and kept in this one file so a reconciliation pass at
 * integration only has to touch here.
 */

// A curated output/input member of the What-If dropdowns (D2: entity
// columns IsExecutiveVisible, joined with AI-enabled).
export interface WhatIfOptionItem {
  id: number;
  name: string;
}

// Trinity-authored example row (D9: ExecutivePrompt, example tool).
export interface WhatIfExampleParams {
  output_id: number;
  input_id: number;
  change_text: string;
}

export interface WhatIfExample {
  id: number;
  title: string;
  sort_order: number;
  params: WhatIfExampleParams;
}

export interface WhatIfOptions {
  outputs: WhatIfOptionItem[];
  inputs: WhatIfOptionItem[];
  examples: WhatIfExample[];
}

// Server-shaped before/after diff for one input (design doc §7 step 7;
// reconciled 2026-08-20 against the landed backend, Agentic-API
// app/ai/v2/executive/input_delta.py::build_input_delta — field names
// below are verbatim from that module's docstring/tests, not the
// earlier provisional `shape`/`value_headers`/`current`/`proposed`
// sketch this file carried before Task 13).
export interface InputDeltaSeries {
  label: string;
  before: (number | null)[];
  after: (number | null)[];
}

export interface InputDeltaTableRow {
  label: string;
  columns: string[];
  before: (number | string | null)[];
  after: (number | string | null)[];
}

// Per-column display metadata for table-kind inputs (backend commit
// 18771f9): the FormatKey to feed getFormattedValue (null = unresolved —
// render the raw value, never guess a format) and whether the column
// earns a bar in the DA-style before/after table (deterministic
// server-side rule: date-bucket columns and the non-magnitude U&E
// parameters are bar: false).
export interface InputDeltaColumnMeta {
  name: string;
  format_key: number | null;
  bar: boolean;
}

export interface InputDeltaInput {
  input_name: string;
  // 'table' covers single_value, uptake_event, and 1-column time_series
  // (build_input_delta's "plottable rule") — never assume 'table' means
  // single_value shaped rows.
  kind: 'time_series' | 'table';
  // x-axis labels verbatim (ValueHeaders) — never date-parsed. Present
  // for both kinds today, but only meaningful for time_series charts.
  x: string[];
  format: 'percent' | 'normal';
  series?: InputDeltaSeries[];
  rows?: InputDeltaTableRow[];
  // Table-kind only; absent on proposals stored before 18771f9 — the
  // grid degrades to unformatted values with bars on.
  columns_meta?: InputDeltaColumnMeta[];
}

export interface InputDelta {
  inputs: InputDeltaInput[];
  total_changed_rows: number;
}

// Honesty-strip / unattempted item: a changes_log-shaped record (Agentic-
// API app/ai/v2/cards/edit_scenarios_change_review.py::ChangeRecord) or
// an UnAttemptedInput ({input_name, reason}). Both are loosely typed and
// consumed defensively (HonestyStrip.tsx) since neither schema is
// `extra="forbid"`-guaranteed stable field-for-field from this side.
export interface WhatIfHonestyItem {
  origin?: 'user_named' | 'expanded';
  input_name?: string;
  reason?: string;
  result_class?: string;
  filters_applied?: Array<Record<string, unknown>> | null;
  column_filter?: string[] | null;
  parameter_filter?: string[] | null;
  [key: string]: unknown;
}

// Body of POST /v2/executive/whatif/propose.
export interface WhatIfProposeRequest {
  model_instance_id: number;
  scenario_id: number;
  // Plural per the controller contract (task-10-brief: `scenario_names?`)
  // even though What-If is single-scenario (D1) — mirrors runExecutiveQuery's
  // shape. Always sent as a one-element array.
  scenario_names: string[];
  output_id: number;
  input_id: number;
  change_text: string;
}

// Backend-composed headline parts (Agentic-API whatif_pipeline.py) — the
// FE builds its own display headline from form values + scenario name
// per the design doc (§4's "If {input} changes — proposed changes to
// {scenario}"); these are carried through for provenance/debugging, not
// literally interpolated into the exec-facing sentence.
export interface WhatIfHeadlineParts {
  change_text: string;
  output_name: string;
  scenario_name: string;
  chart_name: string;
}

// Proposal payload returned by propose and read back by the plan poll
// while status is 'pending_diff_confirmation' (design doc §7 step 8).
export interface WhatIfProposal {
  headline: WhatIfHeadlineParts;
  input_delta: InputDelta;
  // True when the propose pipeline's before/after sandbox blob download
  // failed or came back empty (F7, A5, spec §4 amended): the FE must
  // say so plainly rather than rendering an empty-but-normal-looking
  // before/after visualization. Optional/undefined is treated the same
  // as false — only a real backend that hasn't landed this field yet
  // (or an older stored proposal) should ever omit it.
  input_delta_unavailable?: boolean;
  // False when NO patch modified any row — the proposal is real but there
  // is nothing to recalculate, so Approve must not be offered (ruling
  // 2026-08-21). Distinct from input_delta_unavailable, which is about the
  // preview being renderable, not about whether the write succeeded.
  // Omitted by proposals stored before this field landed; undefined is
  // treated as approvable so reopened older runs aren't retroactively
  // blocked.
  approvable?: boolean;
  honesty: WhatIfHonestyItem[];
  unattempted: WhatIfHonestyItem[];
  // Applied-change projection (whatif_pipeline step 9, added 2026-08-21):
  // one entry per patch that actually wrote (rows_modified > 0), carrying
  // what the system computed — operation, operand, target parameters, and
  // write counts. This is what lets the non-time-series summary state
  // direction/magnitude instead of echoing the user's own words back.
  // rows_modified < matched_rows is the partial-application signal.
  // Optional: proposals stored before the field landed omit it.
  applied?: WhatIfAppliedChange[];
  diff_xlsx_url: string | null;
  diff_xlsx_expires_at?: string | null;
}

export interface WhatIfAppliedChange {
  input_name: string;
  // 'multiply' | 'add' | 'subtract' | 'divide' | 'set' today, but the
  // writer doesn't constrain it — consumers must skip unknown values
  // rather than guess at phrasing.
  operation?: string | null;
  operand?: number | null;
  set_value?: string | number | null;
  value_kind?: string | null;
  // Uptake/Event parameter names the patch targeted (e.g. ["Peak Value"]);
  // null for column-keyed (time-series / single-value) patches.
  parameters?: string[] | null;
  matched_rows?: number;
  rows_modified?: number;
}

// B2 (2026-09-05): propose is kickoff-then-poll. The call returns the run id +
// executing status immediately; the finished proposal is fetched via the
// PREPARING poll (getWhatIfRun -> pending_diff_confirmation + proposal), not
// returned inline.
export interface WhatIfProposeResponse {
  plan_id: string;
  status: string;
}

// Plan lifecycle (design doc §7 "Plan lifecycle (what-if)").
export type WhatIfPlanStatus =
  | 'executing'
  | 'pending_diff_confirmation'
  | 'completed'
  | 'cancelled'
  | 'superseded'
  | 'failed';

// Result payload once a run has completed (RESULT canvas state, §4).
export interface WhatIfResult {
  chart_payload: Record<string, unknown>;
  calculated_at: string;
  xlsx_url: string | null;
  xlsx_url_expires_at?: string | null;
}

// RECALCULATING phase markers (design doc §4 RECALCULATING row: "calm
// phase labels 'Applying your changes…' -> per-calculation-group
// tracker during calc -> 'Preparing your chart…'").
export type WhatIfRecalcPhase = 'applying' | 'calculating' | 'charting';

// Per-group calc snapshot carried on WhatIfProgress.calc while
// phase === 'calculating'. RECONCILED 2026-08-20 against the landed
// Agentic-API app/ai/v2/executive/whatif_executor.py +
// app/ai/v2/forecast/edit_scenarios.py: the exec approve executor's
// `_on_calc_progress(snapshot)` writes `progress["calc"] = snapshot`
// where `snapshot` is exactly the `aggregated` list
// `_wait_and_publish`'s `progress_writer` callback receives —
// `calc_progress.py::_aggregate_rows`'s bare wire-shape output,
// `list[{row_identifier_id, name, total_operations,
// completed_operations, status}]`. This is NOT the `{title, rows}`
// ContentJson wrapper the analyst calc_progress ConversationHistory
// Turn carries (AgenticV2/types.ts CalcProgressContent) — that wrapper
// is built one layer up, in the analyst-only conversation-row emission
// path (`_wait_and_publish`'s `emit_card`/`update_row` branch), which
// the exec path never touches (`publish=None`). `calc` here is the
// bare row array only. CalcProgressRow itself (the per-row shape) is
// identical between both paths and is borrowed straight from
// AgenticV2/types.ts rather than redeclared.
//
// The poll ENVELOPE key names (i.e. that `progress` is nested at
// `WhatIfRunStatus.progress` at all, vs. some other wrapper) remain
// PROVISIONAL until the `GET /v2/executive/whatif/{plan_id}` controller
// route lands — only the approve executor's write side is reconciled
// here.
export interface WhatIfProgress {
  phase?: WhatIfRecalcPhase;
  calc?: CalcProgressRow[] | null;
  // PREPARING stage marker (B2): the propose pipeline stamps which LLM stage
  // is running so the FE poll can render a live stage list. 'segment_selection'
  // -> 'patch_planning'; absent until the first marker is written.
  propose_phase?: 'segment_selection' | 'patch_planning' | string;
  // The propose pipeline also stashes the in-flight proposal onto
  // Progress for poll/reopen while pending_diff_confirmation
  // (whatif_pipeline.py: `update_run_progress(db, plan_id=plan_id,
  // progress={"proposal": proposal})`) — loosely typed since ProposalState
  // already reads the top-level `proposal` field on WhatIfRunStatus
  // for that case; this key is carried for shape completeness only.
  proposal?: WhatIfProposal;
}

// GET /v2/executive/whatif/{plan_id} — serves the PROPOSAL poll, the
// RECALCULATING poll, reopening a completed run, and the read-only
// proposal view (design doc §7, open item §11.2).
export interface WhatIfRunStatus {
  plan_id: string;
  status: WhatIfPlanStatus;
  scenario_id: number;
  output_id: number;
  input_id: number;
  change_text: string;
  proposal?: WhatIfProposal;
  result?: WhatIfResult;
  // Calc-phase progress during RECALCULATING (design doc §4, §7 approve
  // executor) — see WhatIfProgress's PROVISIONAL note above.
  progress?: WhatIfProgress | null;
}

// One row of GET /v2/executive/whatif/recent (design doc §4 RESULT,
// §11.5 — cap 10, newest first, any terminal status).
export interface WhatIfRecentRun {
  plan_id: string;
  title: string;
  scenario_name: string;
  status: WhatIfPlanStatus;
  // Which side of Approve an `executing` run is on (2026-08-23 status
  // chips) — the backend derives it from the stored proposal, since
  // Status alone is ambiguous (the propose pipeline and the
  // post-approve recalculation both sit at `executing`). Null for any
  // non-executing status.
  phase: 'preparing' | 'recalculating' | null;
  inserted_at: string;
}

export interface WhatIfRecentResponse {
  runs: WhatIfRecentRun[];
}
